import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { GhostRiskItem } from '@/hooks/useRevenueIntelligence';

interface GhostRiskMonitorProps {
  items: GhostRiskItem[];
}

const getRiskLabel = (probability: number): { label: string; className: string } => {
  if (probability >= 0.8) {
    return { label: 'High', className: 'text-status-danger bg-status-danger/10 border-status-danger/30' };
  }
  if (probability >= 0.6) {
    return { label: 'Medium', className: 'text-status-warning bg-status-warning/10 border-status-warning/30' };
  }
  return { label: 'Low', className: 'text-muted-foreground bg-muted border-border' };
};

export const GhostRiskMonitor = ({ items }: GhostRiskMonitorProps): JSX.Element => {
  return (
    <Card className="h-full">
      <CardHeader className="space-y-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-status-warning" />
          Ghost Risk Monitor
        </CardTitle>
        <p className="text-sm text-muted-foreground">Leads likely to go silent</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground">No ghost risk alerts right now.</p>
        )}
        {items.map((item) => {
          const risk = getRiskLabel(item.ghostProbability);
          return (
            <div key={item.draftId} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {item.leadName} · {item.leadCompany}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.daysInactive}d inactive · {item.recommendedAction}
                  </p>
                </div>
                <Badge variant="outline" className={risk.className}>
                  {risk.label} ({Math.round(item.ghostProbability * 100)}%)
                </Badge>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
